import { useEffect } from 'react';

export interface CounterHotkeyHandlers {
  onCustomerSearch?: () => void;
  onProductSearch?: () => void;
  onPayment?: () => void;
  onComplete?: () => void;
  onEscape?: () => void;
  onMoveSelection?: (delta: 1 | -1) => void;
  onAdjustQuantity?: (delta: 1 | -1) => void;
  onRemoveLine?: () => void;
  /** Off while a dialog owns the keyboard, or when the screen is not the counter. */
  enabled?: boolean;
}

/** Keys a barcode scanner can produce. Anything here must never reach a global handler. */
const FUNCTION_KEYS = new Set(['F2', 'F3', 'F4', 'F8']);

/**
 * The counter's keyboard.
 *
 * The hard constraint is not which keys to pick — it is that a barcode scanner is a keyboard.
 * It types the code and presses Enter, fast, into whatever has focus. Two consequences shape
 * everything here.
 *
 * Enter is never a global shortcut. If it completed the sale, a scan arriving a moment after the
 * cashier tabbed away would take payment. Completing is F8, and Enter only ever submits whatever
 * field is focused.
 *
 * And a global shortcut only fires when the event is not going into a form control. The usual
 * approach — timing keystrokes to guess whether a human or a scanner is typing — is a heuristic
 * that fails on a slow USB hub. Since the scan box holds focus for the whole scanning loop, the
 * target check is exact rather than probabilistic: a scanner physically cannot reach this layer.
 * Function keys and Escape are the exceptions, because no scanner emits them.
 */
export function useCounterHotkeys(handlers: CounterHotkeyHandlers): void {
  const { enabled = true } = handlers;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const typing = isTypingTarget(event.target);

      // Escape always lands: closing a dialog is exactly what someone typing in it wants.
      if (event.key === 'Escape') {
        handlers.onEscape?.();
        return;
      }

      if (FUNCTION_KEYS.has(event.key)) {
        // F3 is the browser's Find. On the counter it is the product search, and a cashier does not
        // use Find on a till — but the default has to be stopped explicitly.
        event.preventDefault();

        if (event.key === 'F2') handlers.onCustomerSearch?.();
        if (event.key === 'F3') handlers.onProductSearch?.();
        if (event.key === 'F4') handlers.onPayment?.();
        if (event.key === 'F8') handlers.onComplete?.();
        return;
      }

      // Everything below is a printable or navigation key a scanner could produce, so it only
      // applies when nothing is being typed into.
      if (typing) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          handlers.onMoveSelection?.(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          handlers.onMoveSelection?.(-1);
          break;
        case '+':
        case '=':
          event.preventDefault();
          handlers.onAdjustQuantity?.(1);
          break;
        case '-':
          event.preventDefault();
          handlers.onAdjustQuantity?.(-1);
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          handlers.onRemoveLine?.();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, handlers]);
}

/** Whether the event is going into something a person — or a scanner — is typing in. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
